import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const getSystemTheme = () => {
  if (
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  ) {
    return 'dark';
  }
  return 'light';
};

// Apply theme immediately to prevent flash
const applyTheme = mode => {
  if (typeof window === 'undefined') return;

  const root = window.document.documentElement;
  root.classList.remove('light', 'dark');

  let themeToApply;
  if (mode === 'system') {
    themeToApply = getSystemTheme();
  } else {
    themeToApply = mode;
  }

  root.classList.add(themeToApply);
  return themeToApply;
};

export const ThemeProvider = ({ children }) => {
  const [themeMode, setThemeMode] = useState(() => {
    // Check localStorage first
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme === 'light' || savedTheme === 'dark' || savedTheme === 'system') {
        // Apply immediately
        applyTheme(savedTheme);
        return savedTheme;
      }
    }
    // Default to system
    applyTheme('system');
    return 'system';
  });

  const [resolvedTheme, setResolvedTheme] = useState(() => {
    if (themeMode === 'system') {
      return getSystemTheme();
    }
    return themeMode;
  });

  useEffect(() => {
    const resolved = applyTheme(themeMode);
    setResolvedTheme(resolved);

    if (typeof window !== 'undefined') {
      localStorage.setItem('theme', themeMode);
    }
  }, [themeMode]);

  useEffect(() => {
    if (themeMode !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = e => {
      const systemTheme = e.matches ? 'dark' : 'light';
      const root = window.document.documentElement;
      root.classList.remove('light', 'dark');
      root.classList.add(systemTheme);
      setResolvedTheme(systemTheme);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themeMode]);

  const setTheme = mode => {
    if (mode === 'light' || mode === 'dark' || mode === 'system') {
      applyTheme(mode);
      setThemeMode(mode);
    }
  };

  const toggleTheme = () => {
    setThemeMode(prev => {
      if (prev === 'light') {
        applyTheme('dark');
        return 'dark';
      }
      if (prev === 'dark') {
        applyTheme('system');
        return 'system';
      }
      applyTheme('light');
      return 'light';
    });
  };

  const value = {
    theme: themeMode,
    resolvedTheme,
    setTheme,
    toggleTheme,
    isDark: resolvedTheme === 'dark',
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
